import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useLiveStats, useStats, STATS_QUERY_KEY } from '../hooks/useApi';
import StatCard from '../components/StatCard';
import { useQueryClient } from '@tanstack/react-query';

export default function DashboardPage() {
  const { data, isLoading, error } = useStats();
  const { data: live } = useLiveStats();
  const queryClient = useQueryClient();

  useEffect(() => {
    const status = live?.ingestStatus?.status;
    if (status === 'completed' || status === 'error') {
      queryClient.invalidateQueries({ queryKey: STATS_QUERY_KEY });
    }
  }, [live?.ingestStatus?.status, queryClient]);

  const activeCount =
    data?.byStatus.find((s) => s.status.toLowerCase() === 'active')?.count ?? 0;

  const states = [...(data?.byState ?? [])].sort((a, b) => b.count - a.count);
  const topState = states[0];
  const topStates = states.slice(0, 5);

  const nics = [...(data?.byNic ?? [])].sort((a, b) => b.count - a.count);
  const topNic = nics[0];

  const hasData = typeof data?.total === 'number' && data.total > 0;

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--fg)]">Dashboard</h1>
          <p className="mt-1 text-[var(--muted)]">
            Overview of the MSME / RoC dataset and ingestion pipeline.
          </p>
        </div>
        <Link
          to="/ingest"
          className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-fg)] transition hover:opacity-90"
        >
          Start ingestion
        </Link>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-[var(--danger)]/30 bg-[var(--danger)]/10 px-4 py-3 text-sm text-[var(--danger)]">
          Failed to load stats: {error.message}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Records"
          value={isLoading ? '…' : (data?.total ?? 0).toLocaleString()}
          subtitle="records ingested"
        />
        <StatCard
          title="States / UTs"
          value={isLoading ? '…' : states.length.toLocaleString()}
          subtitle="unique locations"
        />
        <StatCard
          title="Top State"
          value={isLoading ? '…' : topState?.state ?? '—'}
          subtitle={topState ? `${topState.count.toLocaleString()} records` : 'no data yet'}
        />
        <StatCard
          title="Top NIC"
          value={isLoading ? '…' : topNic?.nic ?? '—'}
          subtitle={topNic ? `${topNic.count.toLocaleString()} records` : 'no data yet'}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          title="Active Records"
          value={isLoading ? '…' : activeCount.toLocaleString()}
          subtitle="status = Active"
        />
        <StatCard
          title="Ingestion"
          value={live?.ingestStatus?.status ?? 'idle'}
          subtitle={
            live?.ingestStatus?.jobId
              ? `job ${live.ingestStatus.jobId.slice(0, 8)}…`
              : 'no recent job'
          }
        />
        <StatCard
          title="Status Groups"
          value={isLoading ? '…' : (data?.byStatus.length ?? 0).toLocaleString()}
          subtitle="distinct statuses"
        />
      </div>

      {!hasData && !isLoading && (
        <div className="mt-6 rounded-xl border border-dashed border-[var(--border)] bg-[var(--card)]/60 p-8 text-center">
          <p className="text-lg font-medium text-[var(--fg)]">Dataset is empty</p>
          <p className="mt-1 text-sm text-[var(--muted)]">
            No records have been ingested yet. Start an ingestion job to populate
            the dashboard.
          </p>
          <Link
            to="/ingest"
            className="mt-4 inline-block rounded-lg bg-[var(--primary)] px-5 py-2 text-sm font-medium text-[var(--primary-fg)] transition hover:opacity-90"
          >
            Start first ingestion
          </Link>
        </div>
      )}

      {hasData && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="dashboard-card">
            <h2 className="text-sm font-semibold text-[var(--card-fg)]">
              Records by State (Top {topStates.length})
            </h2>
            <div className="mt-4 h-80">
              {isLoading ? (
                <div className="flex h-full items-center justify-center text-[var(--muted)]">
                  Loading chart…
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topStates}
                    margin={{ top: 8, right: 8, bottom: 40, left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis
                      dataKey="state"
                      angle={-45}
                      textAnchor="end"
                      tick={{ fill: 'var(--muted)', fontSize: 12 }}
                      axisLine={{ stroke: 'var(--border)' }}
                      tickLine={{ stroke: 'var(--border)' }}
                      height={60}
                    />
                    <YAxis
                      tick={{ fill: 'var(--muted)', fontSize: 12 }}
                      axisLine={{ stroke: 'var(--border)' }}
                      tickLine={{ stroke: 'var(--border)' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--card)',
                        borderColor: 'var(--border)',
                        borderRadius: '0.5rem',
                        color: 'var(--card-fg)',
                      }}
                      itemStyle={{ color: 'var(--card-fg)' }}
                    />
                    <Bar dataKey="count" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="dashboard-card">
            <h2 className="text-sm font-semibold text-[var(--card-fg)]">
              Records by Status
            </h2>
            <div className="mt-4 h-80">
              {isLoading ? (
                <div className="flex h-full items-center justify-center text-[var(--muted)]">
                  Loading chart…
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data?.byStatus ?? []}
                    margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis
                      dataKey="status"
                      tick={{ fill: 'var(--muted)', fontSize: 12 }}
                      axisLine={{ stroke: 'var(--border)' }}
                      tickLine={{ stroke: 'var(--border)' }}
                    />
                    <YAxis
                      tick={{ fill: 'var(--muted)', fontSize: 12 }}
                      axisLine={{ stroke: 'var(--border)' }}
                      tickLine={{ stroke: 'var(--border)' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--card)',
                        borderColor: 'var(--border)',
                        borderRadius: '0.5rem',
                        color: 'var(--card-fg)',
                      }}
                      itemStyle={{ color: 'var(--card-fg)' }}
                    />
                    <Bar dataKey="count" fill="var(--success)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {live?.ingestStatus && (
        <div className="mt-6 dashboard-card">
          <h2 className="text-sm font-semibold text-[var(--card-fg)]">
            Recent Ingestion
          </h2>
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <div>
              <p className="text-[var(--muted)]">Job ID</p>
              <p className="font-medium text-[var(--fg)]">
                {live.ingestStatus.jobId}
              </p>
            </div>
            <div>
              <p className="text-[var(--muted)]">Status</p>
              <p className="font-medium text-[var(--fg)]">
                {live.ingestStatus.status}
              </p>
            </div>
            <div>
              <p className="text-[var(--muted)]">Processed</p>
              <p className="font-medium text-[var(--fg)]">
                {(live.ingestStatus.fetchedRecords ?? 0).toLocaleString()} /{' '}
                {(live.ingestStatus.totalRecords ?? 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-[var(--muted)]">Progress</p>
              <p className="font-medium text-[var(--fg)]">
                {live.ingestStatus.percentComplete ?? 0}%
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
